import React, { useState, useCallback, useEffect, useRef } from 'react';
import { syncPortsFromHardware } from '../../../api';
import { PortActivationBadge, getEffectiveActivationStatus, isSimConvertedYes, shouldShowActivate, canRequestSimConversion, isSimConversionRequested } from '../../../shared/portActivation';
import ConvertToTtOtpModal from './ConvertToTtOtpModal';

const SYNC_BTN_STYLE = {
  fontWeight: 700,
  fontSize: '13px',
  padding: '0.35rem 0.85rem',
  borderRadius: '6px',
  border: '2px solid #e65c00',
  background: 'linear-gradient(135deg,#ff8c00,#e65c00)',
  color: '#fff',
  boxShadow: '0 2px 6px rgba(230,92,0,0.4)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export default function UserPortStatusPanel({
  ports = [],
  portStatus = {},
  portCountdowns = {},
  onActivatePort,
  onRefreshActivity,
  onRequestSimConversion,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [forcingPortId, setForcingPortId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // { total, message }
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const syncResultTimer = useRef(null);
  const [conversionModal, setConversionModal] = useState({ open: false, port: null });

  useEffect(() => () => clearTimeout(syncResultTimer.current), []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await onRefreshActivity?.();
      setLastSyncTime(new Date());
    } finally {
      setRefreshing(false);
    }
  }, [onRefreshActivity]);

  const handleSyncFromHardware = useCallback(async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await syncPortsFromHardware();
      setLastSyncTime(new Date());
      setSyncResult(result);
      // Refresh displayed statuses after sync
      await onRefreshActivity?.();
      syncResultTimer.current = setTimeout(() => setSyncResult(null), 6000);
    } catch (err) {
      setSyncResult({ total: 0, message: err?.response?.data?.detail || 'Sync failed' });
      syncResultTimer.current = setTimeout(() => setSyncResult(null), 6000);
    } finally {
      setSyncing(false);
    }
  }, [onRefreshActivity]);

  const handleForceActivate = useCallback(
    async (port) => {
      setForcingPortId(port.id);
      try {
        await onActivatePort?.(port);
      } finally {
        setForcingPortId(null);
      }
    },
    [onActivatePort]
  );

  const activePorts = ports.filter((p) => p.assigned_number && !p.archived);
  if (activePorts.length === 0) return null;

  // Derive queue summary for the banner
  const queuedPorts = activePorts.filter((p) => (portStatus[p.id]?.status) === 'queued');
  const activatingPorts = activePorts.filter((p) => (portStatus[p.id]?.status) === 'activating');
  const activeCount = activePorts.filter((p) => (portStatus[p.id]?.status) === 'active').length;
  const inactiveCount = activePorts.filter(
    (p) => !portStatus[p.id] || portStatus[p.id]?.status === 'inactive'
  ).length;

  return (
    <div className="card mb-3 shadow-sm">
      <div
        className="card-header d-flex justify-content-between align-items-center py-2 gap-2"
        style={{ backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107' }}
      >
        {/* Clickable title area */}
        <div
          className="d-flex align-items-center gap-2 flex-grow-1"
          style={{ cursor: 'pointer' }}
          onClick={() => setIsOpen((open) => !open)}
        >
          <strong style={{ fontSize: '14px' }}>
            {isOpen ? '▼' : '▶'} SIM Box Status
          </strong>
          <span className="text-muted fw-normal" style={{ fontSize: '12px' }}>
            {activeCount > 0 && <span className="text-success me-2">● {activeCount} active</span>}
            {activatingPorts.length > 0 && (
              <span className="text-warning me-2">◑ {activatingPorts.length} activating</span>
            )}
            {queuedPorts.length > 0 && (
              <span className="text-warning me-2">⏳ {queuedPorts.length} queued</span>
            )}
            {inactiveCount > 0 && <span className="text-secondary">○ {inactiveCount} inactive</span>}
          </span>
        </div>

        {/* Always-visible sync button — does NOT toggle the panel */}
        <button
          type="button"
          style={syncing ? { ...SYNC_BTN_STYLE, opacity: 0.7 } : SYNC_BTN_STYLE}
          onClick={(e) => { e.stopPropagation(); handleSyncFromHardware(); }}
          disabled={syncing || refreshing}
          title="Pull live state from the SIM box hardware and fix any stale statuses"
        >
          {syncing ? (
            <><span className="spinner-border spinner-border-sm me-1" />Syncing…</>
          ) : (
            '⚡ Sync from Hardware'
          )}
        </button>
      </div>

      {isOpen && (
        <div className="card-body p-3">
          {/* Controls row */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div className="text-muted small">
              {activePorts.length} number{activePorts.length !== 1 ? 's' : ''} assigned
              {lastSyncTime && (
                <span className="ms-2" style={{ fontSize: '11px' }}>
                  · Last synced: {lastSyncTime.toLocaleTimeString()}
                </span>
              )}
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleRefresh}
              disabled={refreshing || syncing}
              title="Refresh status from database (no hardware call)"
            >
              {refreshing ? (
                <><span className="spinner-border spinner-border-sm me-1" />Refreshing…</>
              ) : (
                '↺ Refresh Status'
              )}
            </button>
          </div>

          {/* Sync feedback */}
          {syncResult && (
            <div
              className={`alert py-2 px-3 mb-3 small ${
                syncResult.total > 0 ? 'alert-success' : 'alert-info'
              }`}
            >
              {syncResult.total > 0
                ? `✅ ${syncResult.message} — dashboard now reflects hardware state.`
                : syncResult.message || 'Already in sync with hardware.'}
            </div>
          )}

          {/* Queue info banner */}
          {queuedPorts.length > 0 && (
            <div className="alert alert-warning py-2 px-3 mb-3 small">
              <strong>⏳ Activation Queue:</strong>{' '}
              {queuedPorts.length} number{queuedPorts.length !== 1 ? 's are' : ' is'} waiting in
              queue. Activations happen one slot at a time — each holds for at least 60 seconds
              before the next number is activated.
            </div>
          )}

          {/* Status table */}
          <div className="table-responsive">
            <table className="table table-sm table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Phone Number</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Queue / ETA</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {activePorts.map((port) => {
                  const s = portStatus[port.id] || {};
                  const status = getEffectiveActivationStatus(s);
                  const portIdNum = Number(port.id);
                  const countdown = portCountdowns[portIdNum] ?? portCountdowns[port.id];
                  const isForcing = forcingPortId === port.id;
                  const isStuck = status === 'active' || status === 'activating';
                  const remaining = s.remaining_seconds ?? countdown;

                  // Queue / ETA cell
                  let queueCell = <span className="text-muted small">—</span>;
                  if (status === 'queued') {
                    queueCell = (
                      <span className="small">
                        {remaining != null ? (
                          <>
                            ETA ~{remaining}s
                            <br />
                            <span className="text-muted" style={{ fontSize: '11px' }}>
                              waiting for active slot
                            </span>
                          </>
                        ) : (
                          <span className="text-muted">In queue</span>
                        )}
                      </span>
                    );
                  } else if (status === 'active' && s.show_30s_warning) {
                    queueCell = (
                      <span className="small text-warning">
                        Next in ~{remaining ?? '30'}s
                      </span>
                    );
                  } else if (status === 'activating') {
                    queueCell = (
                      <span className="small text-muted">
                        Warming up{remaining ? ` (${remaining}s)` : ''}
                      </span>
                    );
                  }

                  return (
                    <tr key={port.id}>
                      <td className="font-monospace">{port.assigned_number}</td>
                      <td className="text-muted small">{port.user_email || '-'}</td>
                      <td>
                        <PortActivationBadge
                          statusEntry={s}
                          countdown={countdown}
                          compact
                          showTrafficLight={false}
                        />
                      </td>
                      <td>{queueCell}</td>
                      <td>
                        <div className="d-flex flex-column gap-1 align-items-start">
                        {!shouldShowActivate(port) ? (
                          <span className="badge bg-secondary" title={isSimConvertedYes(port) ? 'SIM already converted' : 'Service number'}>
                            {isSimConvertedYes(port) ? 'Converted' : 'Service'}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className={`btn btn-sm ${isStuck ? 'btn-warning' : status === 'queued' ? 'btn-outline-secondary' : 'btn-success'}`}
                            disabled={isForcing || status === 'queued'}
                            onClick={() => handleForceActivate(port)}
                            title={
                              status === 'queued'
                                ? 'Already queued — waiting for its turn'
                                : isStuck
                                ? 'Force a fresh activation (clears stuck state)'
                                : 'Activate this number '
                            }
                          >
                            {isForcing ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" />
                                Activating...
                              </>
                            ) : status === 'queued' ? (
                              'In Queue'
                            ) : isStuck ? (
                              'Force Activate'
                            ) : (
                              'Activate'
                            )}
                          </button>
                        )}
                        {!!onRequestSimConversion && canRequestSimConversion(port) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => setConversionModal({ open: true, port })}
                            title="Request conversion of this SIM to a TT OTP number"
                            style={{ fontWeight: 700, borderWidth: 2, fontSize: '0.7rem' }}
                          >
                            Convert to TT OTP
                          </button>
                        )}
                        {isSimConversionRequested(port) && !isSimConvertedYes(port) && (
                          <span className="badge" style={{ backgroundColor: '#fd7e14', fontSize: '0.65rem' }}>
                            Conversion requested
                          </span>
                        )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="mt-2 text-muted" style={{ fontSize: '11px' }}>
            <strong>Status guide:</strong> 🟢 Active = live on hardware · ⏳ Queued = waiting for
            its turn · Activating = warming up (20s) · ⚪ Inactive = not active on hardware.
            Use <strong>Sync from Hardware</strong> if the dashboard status doesn't match what you
            expect — this pulls the live state directly from the SIM box.
          </div>
        </div>
      )}
      <ConvertToTtOtpModal
        open={conversionModal.open}
        port={conversionModal.port}
        isStaff={false}
        onClose={() => setConversionModal({ open: false, port: null })}
        onConfirm={async () => {
          const port = conversionModal.port;
          if (!port || !onRequestSimConversion) return;
          await onRequestSimConversion(port);
        }}
      />
    </div>
  );
}
